/**
 * ErrorBoundary Component
 *
 * Catches React errors and displays user-friendly error messages
 * Hides technical details in production, shows them in development
 */

import { Component, type ReactNode, type ErrorInfo } from 'react'

import { Button } from './ui'

interface ErrorBoundaryProps {
  children: ReactNode
  /** Custom fallback UI */
  fallback?: (error: Error, errorInfo: ErrorInfo, reset: () => void) => ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

function ErrorDevDetails({ error, errorInfo }: { error: Error; errorInfo: ErrorInfo }): JSX.Element | null {
  if (!import.meta.env.DEV) { return null }
  return (
    <div className="mb-6 bg-background rounded-lg p-4 border border-border">
      <h2 className="text-sm font-semibold text-foreground mb-2">Error Details (Development Only)</h2>
      <div className="space-y-3">
        <div>
          <p className="text-xs font-medium text-foreground-secondary mb-1">Message:</p>
          <p className="text-xs text-red-600 dark:text-red-400 font-mono bg-surface p-2 rounded border border-red-200 dark:border-red-900">{error.message}</p>
        </div>
        {error.stack && (
          <div>
            <p className="text-xs font-medium text-foreground-secondary mb-1">Stack Trace:</p>
            <pre className="text-xs text-foreground-secondary font-mono bg-surface p-2 rounded border border-border overflow-x-auto max-h-48 overflow-y-auto">{error.stack}</pre>
          </div>
        )}
        {errorInfo.componentStack && (
          <div>
            <p className="text-xs font-medium text-foreground-secondary mb-1">Component Stack:</p>
            <pre className="text-xs text-foreground-secondary font-mono bg-surface p-2 rounded border border-border overflow-x-auto max-h-48 overflow-y-auto">{errorInfo.componentStack}</pre>
          </div>
        )}
      </div>
    </div>
  )
}

function DefaultErrorUI({ error, errorInfo, onReset }: { error: Error; errorInfo: ErrorInfo; onReset: () => void }): JSX.Element {
  return (
    <div className="h-screen overflow-y-auto bg-background flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-surface rounded-lg shadow-lg p-8">
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
            </svg>
          </div>
        </div>
        <h1 className="text-2xl font-bold text-foreground text-center mb-2">Oops! Something went wrong</h1>
        <p className="text-foreground-secondary text-center mb-6">We encountered an unexpected error. Please try again or contact support if the problem persists.</p>
        <ErrorDevDetails error={error} errorInfo={errorInfo} />
        <div className="flex gap-3 justify-center">
          <Button variant="secondary" onClick={() => window.location.reload()}>Reload Page</Button>
          <Button variant="primary" onClick={onReset}>Try Again</Button>
        </div>
        <div className="mt-6 pt-6 border-t border-border">
          <p className="text-sm text-muted-foreground text-center">
            Need help? Contact support or{' '}
            <a className="text-primary hover:underline" href="/">return to home</a>
          </p>
        </div>
      </div>
    </div>
  )
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (import.meta.env.DEV) {
       
      console.error('ErrorBoundary caught an error:', error, errorInfo)
    }
    this.setState({ error, errorInfo })
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null })
  }

  render(): ReactNode {
    const { hasError, error, errorInfo } = this.state
    const { children, fallback } = this.props

    if (hasError && error && errorInfo) {
      if (fallback) { return fallback(error, errorInfo, this.handleReset) }
      return <DefaultErrorUI error={error} errorInfo={errorInfo} onReset={this.handleReset} />
    }

    return children
  }
}

export default ErrorBoundary
